import React, { useState, useEffect, useMemo } from 'react';
import { Layout } from '@/components/common/Layout';
import { ChatInterface } from '@/components/chat/ChatInterface';
import { SettingsInterface } from '@/components/settings/SettingsInterface';
import { AnalyticsDashboard } from '@/components/analytics/AnalyticsDashboard';
import { WelcomePage } from '@/components/WelcomePage';
import { ConfigurationPage } from '@/components/ConfigurationPage';
import { UnconfiguredNotice } from '@/components/common/UnconfiguredNotice';
import { CONFIG_RECHECK_MS, setupGate } from '@/utils/setupGate';
import { LoginPage } from '@/components/auth/LoginPage';
import { ForgotPasswordPage } from '@/components/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '@/components/auth/ResetPasswordPage';
import { EmailVerificationPage } from '@/components/auth/EmailVerificationPage';
import { useAppStore, resetAppStore } from '@/stores/appStore';
import { useAuthStore } from '@/stores/authStore';
import { useMCPServers } from '@/hooks/useMCPServers';
import { useTranslation } from '@/i18n';
import { apiClient } from '@/utils/apiClient';
import { ToastContainer } from '@/components/common/ToastContainer';
import { ShortcutsModal } from '@/components/common/ShortcutsModal';
import { toast } from '@/stores/toastStore';
import { MOD } from '@/utils/shortcuts';
import { exchangeSsoCodeOnce, readSsoCallback, ssoErrorMessageKey } from '@/utils/ssoCallback';

type Page = 'welcome' | 'login' | 'forgot-password' | 'reset-password' | 'verify-email' | 'chat' | 'settings' | 'analytics';

/**
 * Parse URL query-string for token-based flows.
 */
function useQueryParams() {
  return useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      token: params.get('token'),
      sso: readSsoCallback(window.location.search),
      path: window.location.pathname,
    };
  }, []);
}

function App() {
  const { settings } = useAppStore();
  const { isAuthenticated, token: authToken, user, logout, loadProfile } = useAuthStore();
  const { refreshServerStatus } = useMCPServers();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [paletteQuery, setPaletteQuery] = useState('');
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const { t } = useTranslation();
  const query = useQueryParams();

  // True once apiClient has the auth token wired (avoids premature API calls)
  const [authReady, setAuthReady] = useState(false);

  // System configuration status (null = loading, true = configured, false = needs setup)
  const [systemConfigured, setSystemConfigured] = useState<boolean | null>(null);

  // Check system configuration on mount
  useEffect(() => {
    apiClient.getConfigStatus()
      .then(status => setSystemConfigured(status.configured))
      .catch(() => setSystemConfigured(true)); // If backend unreachable, assume configured (let normal flow handle errors)
  }, []);

  // A non-admin on an unconfigured system sees a notice and cannot fix it
  // themselves: re-check while it is shown, so it clears once an admin
  // finishes setup — every 30 s and when the tab comes back into view.
  // A failed check keeps the notice (unlike the first check, which assumes
  // configured): a network blip must not hide it.
  const noticeShown = setupGate(systemConfigured, user?.role) === 'notice';
  useEffect(() => {
    if (!noticeShown) return;
    const recheck = () => {
      apiClient.getConfigStatus()
        .then(status => { if (status.configured) setSystemConfigured(true); })
        .catch(() => {});
    };
    const onVisible = () => { if (document.visibilityState === 'visible') recheck(); };
    const timer = window.setInterval(recheck, CONFIG_RECHECK_MS);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [noticeShown]);

  // Derive the current page from auth state and an explicit navigation state.
  // Restore from URL hash so that a browser refresh preserves the view.
  const [explicitPage, setExplicitPage] = useState<Page | null>(() => {
    const hash = window.location.hash.replace('#', '').split('/')[0];
    if (hash === 'settings') return 'settings';
    if (hash === 'analytics') return 'analytics';
    return null;
  });
  // Pre-fill email on the login page after verification
  const [loginEmail, setLoginEmail] = useState<string | undefined>(undefined);

  // Check for deep-link routes on first load
  useEffect(() => {
    if (query.path.startsWith('/verify-email') && query.token) {
      setExplicitPage('verify-email');
    } else if (query.path.startsWith('/reset-password') && query.token) {
      setExplicitPage('reset-password');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const currentPage: Page = (() => {
    // Deep-linked routes take priority regardless of auth state
    if (explicitPage === 'verify-email') return 'verify-email';
    if (explicitPage === 'reset-password') return 'reset-password';
    if (explicitPage === 'forgot-password') return 'forgot-password';

    if (!isAuthenticated) {
      if (explicitPage === 'login') return 'login';
      return 'welcome';
    }
    if (explicitPage === 'settings') return 'settings';
    if (explicitPage === 'analytics') return 'analytics';
    if (explicitPage === 'chat' || explicitPage === null) return 'chat';
    return 'chat';
  })();

  const currentView: 'chat' | 'settings' | 'analytics' = currentPage === 'settings'
    ? 'settings'
    : currentPage === 'analytics'
    ? 'analytics'
    : 'chat';

  const handleViewChange = (view: 'chat' | 'settings' | 'analytics') => {
    setExplicitPage(view);
    window.history.replaceState(null, '', `#${view}`);
  };

  /** Clear deep-link params from URL without triggering a reload. */
  const clearUrlParams = () => {
    if (window.location.search || window.location.pathname !== '/') {
      window.history.replaceState({}, '', '/');
    }
  };

  // Hydrate auth token into apiClient on mount.
  // Also handle SSO callback tokens from IdP redirects.
  useEffect(() => {
    const validateAndWire = async () => {
      // SSO callback: ?sso_code=<one-time code> → POST for the session token
      // (STORY-349 — the token itself never travels in the URL).
      if (query.sso.code) {
        try {
          const ssoToken = await exchangeSsoCodeOnce(query.sso.code, (c) => apiClient.exchangeSsoCode(c));
          apiClient.setAuthToken(ssoToken);
          // Pass token explicitly to avoid Zustand hydration race —
          // persist middleware may overwrite the store before loadProfile
          // reads get().token, so we bypass it entirely.
          await loadProfile(ssoToken);
        } catch {
          toast.error(t.auth.ssoFailed);
        }
        clearUrlParams();
      } else if (query.sso.error) {
        toast.error(t.auth[ssoErrorMessageKey(query.sso.error)]);
        clearUrlParams();
      } else if (authToken) {
        apiClient.setAuthToken(authToken);
        await loadProfile();
      }
      apiClient.setOnUnauthorized(() => {
        logout();
        setExplicitPage('welcome');
      });
      setAuthReady(true);
    };
    validateAndWire();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Load chat sessions, settings & LLM providers when user logs in ----
  // Sessions/settings come from the server DB. Providers must also be synced:
  // credentials live on the backend, but the UI list used to be localStorage-only.
  const { loadSessionsFromServer, loadSettingsFromServer, syncLLMProvidersFromBackend } = useAppStore();
  useEffect(() => {
    if (user?.id) {
      loadSessionsFromServer();
      // Settings first (may contain selectedLLMProvider), then merge backend providers
      (async () => {
        await loadSettingsFromServer();
        await syncLLMProvidersFromBackend();
      })();
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- First-use shortcut hint (shown once per browser) ----
  useEffect(() => {
    if (!user?.id) return;
    const key = 'shortcuts-hint-shown';
    if (localStorage.getItem(key)) return;
    const timer = setTimeout(() => {
      toast.info(`${MOD}+K — commands, ${MOD}+/ — shortcuts`, 5000);
      localStorage.setItem(key, '1');
    }, 2000);
    return () => clearTimeout(timer);
  }, [user?.id]);

  // ---- On logout: reset app store (clear in-memory sessions) ----
  useEffect(() => {
    const onLogout = () => {
      resetAppStore();
    };
    window.addEventListener('auth-logout', onLogout);
    return () => window.removeEventListener('auth-logout', onLogout);
  }, []);

  // Apply theme to document
  useEffect(() => {
    const applyTheme = () => {
      const root = document.documentElement;
      if (settings.theme === 'system') {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        root.classList.toggle('dark', prefersDark);
      } else {
        root.classList.toggle('dark', settings.theme === 'dark');
      }
    };
    applyTheme();
    if (settings.theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      mediaQuery.addEventListener('change', applyTheme);
      return () => mediaQuery.removeEventListener('change', applyTheme);
    }
  }, [settings.theme]);

  // Load MCP servers on mount + focus (wait for auth token to be wired first)
  useEffect(() => {
    if (!isAuthenticated || !authReady) return;
    let timer: number | undefined;
    const debounced = () => {
      if (timer) window.clearTimeout(timer);
      // @ts-ignore
      timer = window.setTimeout(() => refreshServerStatus(), 500);
    };
    refreshServerStatus();
    const onFocus = () => debounced();
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      if (timer) window.clearTimeout(timer);
    };
  }, [refreshServerStatus, isAuthenticated, authReady]);

  // Global keyboard shortcuts (Cmd/Ctrl+K palette, Cmd/Ctrl+/ shortcuts)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().includes('MAC');
      const mod = isMac ? e.metaKey : e.ctrlKey;
      if (mod && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(true);
      }
      if (mod && e.key === '/') {
        e.preventDefault();
        setShortcutsOpen(prev => !prev);
      }
      if (e.key === 'Escape') {
        if (shortcutsOpen) setShortcutsOpen(false);
        else setPaletteOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shortcutsOpen]);

  // ---------- font size class ----------
  // Match the Appearance panel labels: small=12px (text-xs), medium=14px
  // (text-sm), large=16px (text-base). Previously "small" also mapped to
  // text-sm, so choosing Small did nothing (STORY-157).
  const fontClass = settings.fontSize === 'small' ? 'text-xs' : settings.fontSize === 'large' ? 'text-base' : 'text-sm';

  // ---------- render deep-link pages ----------

  if (currentPage === 'verify-email' && query.token) {
    return (
      <div className={`App ${fontClass}`}>
        <EmailVerificationPage
          token={query.token}
          onSuccess={(verifiedEmail) => {
            clearUrlParams();
            if (verifiedEmail) setLoginEmail(verifiedEmail);
            setExplicitPage('login');
          }}
        />
      </div>
    );
  }

  if (currentPage === 'reset-password' && query.token) {
    return (
      <div className={`App ${fontClass}`}>
        <ResetPasswordPage
          token={query.token}
          onSuccess={() => {
            clearUrlParams();
            setExplicitPage('login');
          }}
        />
      </div>
    );
  }

  if (currentPage === 'forgot-password') {
    return (
      <div className={`App ${fontClass}`}>
        <ForgotPasswordPage
          onBack={() => setExplicitPage('login')}
        />
      </div>
    );
  }

  // ---------- render auth pages ----------

  if (currentPage === 'welcome') {
    return (
      <div className={`App ${fontClass}`}>
        <WelcomePage onGetStarted={() => setExplicitPage('login')} />
      </div>
    );
  }

  if (currentPage === 'login') {
    return (
      <div className={`App ${fontClass}`}>
        <LoginPage
          onBack={() => setExplicitPage('welcome')}
          onSuccess={() => { setLoginEmail(undefined); setExplicitPage('chat'); }}
          onForgotPassword={() => setExplicitPage('forgot-password')}
          initialEmail={loginEmail}
        />
      </div>
    );
  }

  // ---------- render configuration page if system unconfigured (after auth) ----------
  // Admins only: a non-admin cannot use the setup page (its writes are
  // admin-gated on the backend), so they get the app shell with a notice.

  const gate = setupGate(systemConfigured, user?.role);

  if (gate === 'setup') {
    return (
      <div className={`App ${fontClass}`}>
        <ConfigurationPage onConfigured={() => setSystemConfigured(true)} />
      </div>
    );
  }

  // ---------- Authenticated app shell ----------

  const renderContent = () => {
    switch (currentView) {
      case 'chat':
        return <ChatInterface />;
      case 'settings':
        return <SettingsInterface />;
      case 'analytics':
        return (
          <div className="h-full overflow-y-auto p-6">
            <AnalyticsDashboard />
          </div>
        );
      default:
        return <ChatInterface />;
    }
  };

  return (
    <div className={`App ${fontClass}`}>
      <Layout
        currentView={currentView}
        onViewChange={handleViewChange}
      >
        {gate === 'notice' ? (
          <div className="flex flex-col h-full">
            <UnconfiguredNotice />
            <div className="flex-1 min-h-0">{renderContent()}</div>
          </div>
        ) : (
          renderContent()
        )}
      </Layout>

      {paletteOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 bg-black/30" onClick={() => setPaletteOpen(false)}>
          <div className="w-full max-w-lg mx-4 rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg" onClick={(e) => e.stopPropagation()}>
            <input
              autoFocus
              value={paletteQuery}
              onChange={(e) => setPaletteQuery(e.target.value)}
              placeholder={t.commandPalette.placeholder}
              className="w-full px-3 py-2 bg-transparent border-b border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
            />
            <div className="max-h-64 overflow-y-auto">
              <CommandList query={paletteQuery} onSelect={(cmd) => { setPaletteOpen(false); window.dispatchEvent(new CustomEvent('chat-command', { detail: cmd })); }} />
            </div>
          </div>
        </div>
      )}

      <ShortcutsModal isOpen={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
      <ToastContainer />
    </div>
  );
}

export default App;

// Inline command list component
const CommandList: React.FC<{ query: string; onSelect: (cmd: string) => void }> = ({ query, onSelect }) => {
  const { t } = useTranslation();
  const commands = [
    '/get_runtime_license',
    '/get_runtime_config',
    '/settings',
    '/tool',
    '/server',
  ];
  const q = query.trim().toLowerCase();
  const items = commands.filter(c => !q || c.toLowerCase().includes(q));
  return (
    <ul className="py-2">
      {items.map((c) => (
        <li key={c}>
          <button className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => onSelect(c)}>
            {c}
          </button>
        </li>
      ))}
      {items.length === 0 && (
        <li className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{t.commandPalette.noCommands}</li>
      )}
    </ul>
  );
};
